/** @format */

import { Component, inject, input } from '@angular/core';
import { BOPIS_FILTER, FACET_STORE, FILTER_ID_5004, SALE_FILTER } from '@plp/data-access/constants';
import type { PzPlpBannerItem, PzPlpBannerType } from '@plp/data-access/interfaces';
import { Store } from '@ngrx/store';
import { selectedFiltersUpdated } from '@plp/data-access/state';
import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import { HmfActionTextComponent, HmfTextLinkComponent } from '@dsg-tech/homefield-angular';

/**
 * Renders a single PLP personalized banner (Standard, BOPIS, or Deal Seekers).
 *
 * - Standard: CTAs are rendered as anchor links for navigation.
 * - BOPIS: primary CTA dispatches a BOPIS store-availability filter action.
 * - Deal Seekers: primary CTA dispatches a Sale filter action.
 *
 * The component is stateless: all data flows in via the required `banner` input.
 * Grid positioning (grid-row-start) is applied by the parent ProductListGridComponent.
 *
 * Pattern reference: PinnedContentComponent.
 */
@Component({
  selector: 'plp-components-personalized-banner',
  imports: [NgOptimizedImage, HmfActionTextComponent, HmfTextLinkComponent],
  templateUrl: './personalized-banner.component.html',
  styleUrl: './personalized-banner.component.scss',
})
export class PersonalizedBannerComponent {
  private readonly store = inject(Store);
  private readonly document = inject(DOCUMENT);

  /** The banner data to render. Required -- parent must always supply a value. */
  readonly banner = input.required<PzPlpBannerItem>();

  /**
   * Handles CTA click for BOPIS and Deal Seekers banner types.
   * Dispatches the appropriate filter action and scrolls to the top of the product list.
   */
  handleFilterCta(): void {
    const bannerType: PzPlpBannerType = this.banner().bannerType;
    let selectedFilters: Record<string, string[]> = {};

    switch (bannerType) {
      case 'bopis':
        selectedFilters = { [FACET_STORE]: [BOPIS_FILTER] };
        break;
      case 'dealSeekers':
        selectedFilters = { [FILTER_ID_5004]: [SALE_FILTER] };
        break;
      default:
        return;
    }

    this.store.dispatch(selectedFiltersUpdated({ selectedFilters }));
    queueMicrotask(() => {
      const plpContainerElem = this.document.getElementById('plp-list-container');
      if (plpContainerElem) {
        plpContainerElem.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    });
  }
}
